import { customElement, property, query, state } from 'lit/decorators.js';
import { sbkMetaNavStyle } from './meta-nav.styles';
import { html, LitElement } from 'lit';
import { SbkSearchFlyout } from "@/components/search-flyout/search-flyout.ts";
import { SbkModalFlyout } from "@/components/modal-flyout/modal-flyout.ts";
import { sbkCoreBreakpointXl } from '../../tokens/variables';
import { ClassInfo, classMap } from 'lit/directives/class-map.js';


@customElement('sbk-meta-nav')
export class SbkMetaNav extends LitElement {

    @property({ attribute: 'logo-path' })
    logoPath = ''

    @property({ attribute: 'corporate-logo-path' })
    corporateLogoPath = '';

    @property({ attribute: 'logo-alt-text' })
    logoAltText = ''

    @property({ attribute: 'corporate-logo-alt-text' })
    corporateLogoAltText? = '';

    @property({ attribute: 'logo-link' })
    logoLink = ''

    @property()
    variant: 'default' | 'reduced' | 'reduced_lp' = 'default'

    @property({ type: Boolean, attribute: 'not-sticky' })
    notSticky = false

    @property({ type: Boolean })
    isPinned = false

    @query('#sensor')
    sensorElement!: Element;

    @state()
    private _hasMetaNavItems = false;

    constructor() {
        super();
        this.addEventListener('triggeredSearchFlyout', this._triggeredSearchFlyout)
        this.addEventListener('triggeredModalFlyout', this._triggeredModalFlyout)
    }

    static get styles() {
        return sbkMetaNavStyle;
    }

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('keydown', this._handleKeyDown);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this._handleKeyDown);
    }

    private _handleKeyDown = (event: KeyboardEvent) => {
        const focusableElements = Array.from(this.querySelectorAll('sbk-tab-menu-item')) as HTMLElement[];

        if (!focusableElements.length) return;

        const activeElement = document.activeElement as HTMLElement;
        const currentIndex = focusableElements.indexOf(activeElement);
        if (currentIndex === -1) return;

        let newIndex;
        if (event.key === 'ArrowRight') {
            newIndex = (currentIndex + 1) % focusableElements.length;
        } else if (event.key === 'ArrowLeft') {
            newIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
        } else {
            return;
        }

        event.preventDefault();
        const focusElement = focusableElements[newIndex].shadowRoot?.firstElementChild?.firstElementChild as HTMLElement;
        focusElement?.focus();
    };

    render() {
        if (['reduced', 'reduced_lp'].includes(this.variant)) {
            return html`
                <div id="sensor"></div>
                <div class=${classMap(this.getClassMap())}>
                    <div class="meta-nav__wrapper">
                        ${this.renderLogos()}
                        <div class="meta-nav__wrapper-items">
                            <div class="meta-nav-items">
                                <slot></slot>
                            </div>
                        </div>
                        <div class="meta-nav-actions">
                            <div class="meta-nav-icons ${this._hasMetaNavItems ? '' : 'standAlone'}">
                                <slot name="icons"></slot>
                            </div>
                            <slot name="cta" class="meta-nav-cta"></slot>
                        </div>
                    </div>
                </div>
            `;
        }
        return html`
            <div class=${classMap(this.getClassMap())}>
                <div class="meta-nav__wrapper">
                    ${this.renderLogos()}
                    <div class="meta-nav__wrapper-items">
                        <div class="meta-nav-items">
                            <slot></slot>
                        </div>
                        <div class="meta-nav-icons">
                            <span class="verticalDivider"></span>
                            <slot name="icons"></slot>
                        </div>
                    </div>
                    <slot name="cta" class="meta-nav-cta"></slot>
                </div>
            </div>
        `;
    }


    renderLogos() {
        if (!this.logoPath && !this.corporateLogoPath) {
            return '';
        }
        return html`
            <div class="meta-logo-container">
                ${this.renderLogoLink()}

                ${this.corporateLogoPath ? html`
                    ${this.renderCorporateLogo()}
                ` : ''}
            </div>
        `;
    }

    renderCorporateLogo() {
        if (!this.corporateLogoPath) {
            return '';
        }
        return html`
            <span class="verticalDivider"></span>
            <img src=${this.corporateLogoPath} alt=${this.corporateLogoAltText ? this.corporateLogoAltText : this.logoAltText} class="corporate-logo"/>
        `;
    }

    renderLogoLink() {
        if (!this.logoLink) {
            return this.renderLogo();
        }
        return html`
            <a href=${this.logoLink} class="logo-link">
                ${this.renderLogo()}
            </a>
        `;
    }

    renderLogo() {
        if (!this.logoPath) {
            return '';
        }
        return html`
            <img src=${this.logoPath} alt=${this.logoAltText} class="logo"/>
        `;
    }

    firstUpdated() {
        const slot: HTMLSlotElement | null | undefined = this.shadowRoot?.querySelector('slot');
        const viewWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
        if (slot) {
            this._hasMetaNavItems = slot?.assignedElements({flatten: true}).length > 0;
        }

        if (!this.notSticky && viewWidth <= parseInt(sbkCoreBreakpointXl.replace("px", ""))) {
            this._handleScroll()
        }
    }

    _handleScroll() {
        const observer = new IntersectionObserver(
            ([e]) => {
                if (e.intersectionRatio < 1) {
                    this.isPinned = true
                } else {
                    this.isPinned = false
                }
            },
            { threshold: [1] }
        );
        observer.observe(this.sensorElement);
    }

    _triggeredSearchFlyout(event: Event) {
        const expanded = (<CustomEvent>event).detail?.expanded
        if (expanded) {
            this._hideModalFlyout();
        }
    }

    _triggeredModalFlyout(event: Event) {
        const expanded = (<CustomEvent>event).detail?.expanded
        if (expanded) {
            this._hideSearchFlyout();
        }
    }

    private _hideSearchFlyout() {
        const searchFlyouts: SbkSearchFlyout[] = Array.from(this.querySelectorAll('sbk-search-flyout'))
        searchFlyouts.forEach((searchFlyout) => {
            searchFlyout.expanded = false
            searchFlyout._changeTriggerElement()
        })
    }

    private _hideModalFlyout() {
        const modalFlyouts: SbkModalFlyout[] = Array.from(this.querySelectorAll('sbk-modal-flyout'))
        modalFlyouts.forEach((modalFlyout) => {
            modalFlyout.expanded = false
            modalFlyout._changeTriggerElement()
        })
    }

    getClassMap(): ClassInfo {
        return {
            'meta-nav': true,
            'meta-nav-reduced': this.variant === 'reduced',
            'meta-nav-reduced-lp': this.variant === 'reduced_lp',
            'is-pinned': this.isPinned,
            'not-sticky': this.notSticky,
        };
    }
}
